// Store picker for the pharmacy medicine search. Pair with usePharmacyStore().
//
// Renders nothing when the hospital has exactly one pharmacy store — the hook
// has already selected it, and a dropdown with one option is a click that
// changes nothing. It only appears when there is a real choice to make, or
// when there's no store at all and the search can't run.

import React from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

export default function PharmacyStoreSelect({
  stores = [],
  storeId = "",
  onChange,
  loading = false,
  error = "",
  showPicker = false,
  width = 260,
  label = "Pharmacy store:",
  menuProps,
}) {
  if (error) {
    return (
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#B91C1C" }}>
        {error}
      </Typography>
    );
  }

  if (loading && stores.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
          Loading pharmacy stores…
        </Typography>
      </Box>
    );
  }

  if (!showPicker) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      {label && (
        <Typography
          sx={{
            fontSize: "0.82rem",
            fontWeight: 700,
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      )}
      <FormControl size="small" sx={{ width }}>
        <Select
          displayEmpty
          value={storeId}
          onChange={(e) => onChange?.(e.target.value)}
          MenuProps={menuProps}
          sx={{ fontSize: "0.85rem" }}
          renderValue={(value) => {
            const picked = stores.find((s) => s.id === value);
            if (!picked) {
              return (
                <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                  Select pharmacy store…
                </Typography>
              );
            }
            return (
              <Typography sx={{ fontSize: "0.85rem" }}>{picked.name}</Typography>
            );
          }}
        >
          {stores.map((store) => (
            <MenuItem key={store.id} value={store.id} sx={{ fontSize: "0.85rem" }}>
              {store.name}
              {store.code && (
                <Typography
                  component="span"
                  sx={{ ml: 0.8, fontSize: "0.72rem", color: "#9CA3AF" }}
                >
                  {store.code}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
